import { useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode';
import {
  DELETE_CONFIRMATION,
  countryName,
  playerDisplayName,
  type LeaderboardBoard,
  type LeaderboardScope,
  type PlayerProfile,
  type PlayerRank,
  type PlayerStats,
  type PracticeProgress,
} from '@aiscoreboard/contracts';
import { CountryPicker, Flag } from '../components/CountryPicker';
import { AvatarCropper } from '../components/AvatarCropper';
import {
  closeAccount,
  confirmPassword,
  fetchMyRanks,
  fetchMyStats,
  updateMe,
  uploadAvatar,
} from '../lib/accounts';
import { settingsUrl } from '../lib/handoff';
import { useLocale, useT, type MessageKey } from '../lib/i18n';
import { IMAGE_ACCEPT } from '../lib/images';

interface Props {
  token: string;
  player: PlayerProfile;
  onUpdated: (player: PlayerProfile) => void;
  onClosed: () => void;
  onBack: () => void;
  /**
   * True when this screen was opened on somebody's own phone by scanning the
   * board's pairing or settings code. Opens editing and hides the phone handoff.
   */
  onPhone?: boolean;
}

type Panel = 'overview' | 'stats' | 'edit' | 'danger';

export function ProfileScreen({ token, player, onUpdated, onClosed, onBack, onPhone = false }: Props) {
  const t = useT();
  const [panel, setPanel] = useState<Panel>(onPhone ? 'edit' : 'overview');
  const [error, setError] = useState<string | null>(null);
  /**
   * The password, once it has been proved, for as long as this screen is open.
   *
   * Held in memory and never stored: the board is a shared tablet, and the
   * next person to pick it up must find the settings locked again. Everything
   * that changes the account sends it, so the server checks each change rather
   * than trusting that this screen asked.
   */
  const [password, setPassword] = useState<string | null>(null);

  const locked = (panel === 'edit' || panel === 'danger') && password === null;

  return (
    <main className="mx-auto flex h-full w-full max-w-2xl flex-col gap-3 overflow-y-auto p-4">
      <header className="flex items-center gap-3">
        <button type="button" onClick={onBack} className="btn-ghost px-3 py-2 text-sm">
          ← {t('common.back')}
        </button>
        <h1 className="text-xl font-black">{t('profile.title')}</h1>
      </header>

      <div className="panel flex items-center gap-4">
        <Avatar player={player} size={64} />
        <div className="min-w-0">
          <p className="truncate text-lg font-bold">{playerDisplayName(player)}</p>
          <p className="flex items-center gap-2 text-sm text-white/60">
            <Flag code={player.country} />
            {countryName(player.country)}
          </p>
          {player.club && (
            <p className="truncate text-xs text-white/40">
              {player.club.name} · {player.club.city}
            </p>
          )}
        </div>
        {/* Their own handle, because this is the one screen where a player
            comes looking for what they sign in with. */}
        <p className="ml-auto max-w-[14rem] shrink-0 truncate font-mono text-sm tracking-widest text-ball-yellow">
          {player.username}
        </p>
      </div>

      <nav className="flex shrink-0 flex-wrap gap-2 max-sm:grid max-sm:grid-cols-2">
        {(
          [
            ['overview', 'profile.tabOverview'],
            ['stats', 'profile.tabStats'],
            ['edit', 'profile.tabEdit'],
            ['danger', 'profile.tabDanger'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => {
              setPanel(key);
              setError(null);
            }}
            className={[
              'btn px-3 py-2 text-sm',
              panel === key ? 'bg-white text-hud-bg' : 'bg-white/10 hover:bg-white/20',
            ].join(' ')}
          >
            {t(label)}
          </button>
        ))}
      </nav>

      {(panel === 'overview' || panel === 'stats') && (
        <StatsPanel token={token} detailed={panel === 'stats'} />
      )}
      {panel === 'overview' && <RanksPanel token={token} />}
      {panel === 'overview' && !onPhone && <PhoneHandoff />}
      {locked && <PasswordGate token={token} onUnlocked={setPassword} onError={setError} />}
      {panel === 'edit' && password !== null && (
        <EditPanel
          token={token}
          player={player}
          password={password}
          onUpdated={onUpdated}
          onError={setError}
        />
      )}
      {panel === 'danger' && password !== null && (
        <DangerPanel token={token} password={password} onClosed={onClosed} onError={setError} />
      )}

      {error && <p className="text-sm text-ball-red">{error}</p>}
    </main>
  );
}

export function Avatar({
  player,
  size = 40,
}: {
  player: PlayerProfile;
  /**
   * A number of pixels, or any CSS length — `min(251px, 24vh)` and the like,
   * so a caller can cap a portrait against the screen without measuring it.
   */
  size?: number | string;
}) {
  const initials = `${player.firstName[0] ?? ''}${player.lastName[0] ?? ''}`.toUpperCase();
  const length = typeof size === 'number' ? `${size}px` : size;
  return player.avatarUrl ? (
    <img
      src={player.avatarUrl}
      alt=""
      style={{ width: length, height: length }}
      className="shrink-0 rounded-full object-cover ring-2 ring-white/20"
    />
  ) : (
    <span
      style={{ width: length, height: length, fontSize: `calc(${length} * 0.36)` }}
      className="grid shrink-0 place-items-center rounded-full bg-white/10 font-bold ring-2 ring-white/20"
    >
      {initials}
    </span>
  );
}

const STAT_ROWS: [keyof PlayerStats, MessageKey, (v: number) => string][] = [
  ['rating', 'stat.rating', (v) => String(v)],
  ['matchesPlayed', 'stat.matchesPlayed', (v) => String(v)],
  ['matchesWon', 'stat.matchesWon', (v) => String(v)],
  ['matchWinRate', 'stat.matchWinRate', (v) => `${Math.round(v * 100)}%`],
  ['framesPlayed', 'stat.framesPlayed', (v) => String(v)],
  ['framesWon', 'stat.framesWon', (v) => String(v)],
  ['frameWinRate', 'stat.frameWinRate', (v) => `${Math.round(v * 100)}%`],
  ['pointsScored', 'stat.pointsScored', (v) => String(v)],
  ['pointsConceded', 'stat.pointsConceded', (v) => String(v)],
  ['pointsPerFrame', 'stat.pointsPerFrame', (v) => v.toFixed(1)],
  ['potSuccessRate', 'stat.potSuccessRate', (v) => `${Math.round(v * 100)}%`],
  ['highestBreak', 'stat.highestBreak', (v) => String(v)],
  ['totalBreaks', 'stat.totalBreaks', (v) => String(v)],
  ['averageBreak', 'stat.averageBreak', (v) => v.toFixed(1)],
  ['fifties', 'stat.fifties', (v) => String(v)],
  ['centuries', 'stat.centuries', (v) => String(v)],
  ['foulsConceded', 'stat.foulsConceded', (v) => String(v)],
  ['pointsLostToFouls', 'stat.pointsLostToFouls', (v) => String(v)],
];

function StatsPanel({ token, detailed }: { token: string; detailed: boolean }) {
  const t = useT();
  const [stats, setStats] = useState<PlayerStats | null>(null);
  const [practice, setPractice] = useState<PracticeProgress | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void fetchMyStats(token)
      .then((r) => {
        if (cancelled) return;
        setStats(r.stats);
        setPractice(r.practice);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : t('profile.statsFailed'));
      });
    return () => {
      cancelled = true;
    };
  }, [token, t]);

  if (error) return <p className="text-sm text-ball-red">{error}</p>;
  if (!stats || !practice) return <p className="text-white/50">{t('common.loading')}</p>;

  const rows = detailed ? STAT_ROWS : STAT_ROWS.slice(0, 4);

  return (
    <section className="flex flex-col gap-3">
      <div className="grid grid-cols-3 gap-2">
        <Tile label={t('stat.rating')} value={String(stats.rating)} />
        <Tile label={t('stat.highestBreak')} value={String(stats.highestBreak)} />
        <Tile label={t('stat.centuries')} value={String(stats.centuries)} />
      </div>

      <dl className="panel divide-y divide-white/10">
        {rows.map(([key, label, format]) => (
          <div key={key} className="flex items-baseline justify-between py-2">
            <dt className="text-sm text-white/60">{t(label)}</dt>
            <dd className="font-bold num tabular-nums">{format(stats[key])}</dd>
          </div>
        ))}
      </dl>

      {detailed && (
        <div className="panel">
          <h3 className="mb-2 text-sm uppercase tracking-wider text-white/50">
            {t('profile.practice')}
          </h3>
          <dl className="divide-y divide-white/10">
            {(
              [
                ['stat.sessions', practice.sessions],
                ['stat.challengesAttempted', practice.challengesAttempted],
                ['stat.challengesPassed', practice.challengesPassed],
                ['stat.progressScore', Math.round(practice.progressScore * 100) / 100],
                ['stat.practicePoints', practice.totalPoints],
                ['stat.practiceHighestBreak', practice.highestBreak],
              ] as [MessageKey, number][]
            ).map(([label, value]) => (
              <div key={label} className="flex items-baseline justify-between py-2">
                <dt className="text-sm text-white/60">{t(label)}</dt>
                <dd className="font-bold num tabular-nums">{value}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </section>
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="panel text-center">
      <p className="text-3xl num-lg">{value}</p>
      <p className="text-xs uppercase tracking-wider text-white/50">{label}</p>
    </div>
  );
}

const BOARD_LABELS: Record<LeaderboardBoard, MessageKey> = {
  rating: 'board.rating',
  highest_break: 'board.highestBreak',
  practice: 'board.practice',
};

const SCOPE_LABELS: Record<LeaderboardScope, MessageKey> = {
  global: 'board.global',
  country: 'board.country',
  city: 'board.city',
};

/**
 * Where this player stands, rather than what they scored.
 *
 * A rating is an abstraction until it is a position: "1450" says nothing that
 * "4th of 61 in Berlin" does not say better. The three widths sit side by side
 * because they are three answers to the same question and a club player cares
 * about them in the opposite order to the one a world ranking implies.
 */
function RanksPanel({ token }: { token: string }) {
  const t = useT();
  const [ranks, setRanks] = useState<PlayerRank[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void fetchMyRanks(token)
      .then((r) => {
        if (!cancelled) setRanks(r.ranks);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : t('profile.ranksFailed'));
      });
    return () => {
      cancelled = true;
    };
  }, [token, t]);

  if (error) return <p className="text-sm text-ball-red">{error}</p>;
  if (!ranks) return null;

  const boards: LeaderboardBoard[] = ['rating', 'highest_break', 'practice'];

  return (
    <section className="panel flex flex-col gap-2">
      <h3 className="text-sm uppercase tracking-wider text-white/50">{t('profile.rankings')}</h3>
      {boards.map((board) => {
        const cells = ranks.filter((r) => r.board === board);
        if (cells.length === 0) return null;
        return (
          <div key={board} className="flex flex-wrap items-baseline gap-x-4 gap-y-1 border-t border-white/10 pt-2 first:border-0 first:pt-0">
            <p className="w-32 shrink-0 text-sm text-white/60">{t(BOARD_LABELS[board])}</p>
            {cells.map((cell) => (
              <p key={cell.scope} className="flex items-baseline gap-1 text-sm">
                {cell.scope === 'country' && cell.place && (
                  <Flag code={cell.place} className="h-3 w-4 self-center rounded-[2px]" />
                )}
                <span className="text-white/40">
                  {cell.scope === 'country' && cell.place
                    ? countryName(cell.place)
                    : (cell.place ?? t(SCOPE_LABELS[cell.scope]))}
                  {': '}
                </span>
                <span className="num tabular-nums font-bold text-ball-yellow">
                  {cell.rank === null ? '—' : `#${cell.rank}`}
                </span>
                <span className="text-white/40"> / {cell.total}</span>
              </p>
            ))}
          </div>
        );
      })}
    </section>
  );
}

/**
 * A QR code that opens these same settings on the player's own phone.
 *
 * The board can do everything the phone can, but a shared tablet is a poor
 * place to type a password and a worse one to read a table from across the
 * room. The code carries no session — whoever scans it signs in themselves —
 * so it is safe to leave on screen in a club.
 */
function PhoneHandoff() {
  const t = useT();
  const { locale } = useLocale();
  const [image, setImage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void settingsUrl(locale)
      .then((url) => QRCode.toDataURL(url, { width: 512, margin: 1 }))
      .then((data) => {
        if (!cancelled) setImage(data);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [locale]);

  return (
    <section className="panel flex items-center gap-4">
      {image ? (
        <img src={image} alt="" className="shrink-0 rounded-lg bg-white p-1" width={104} />
      ) : (
        <div className="h-[104px] w-[104px] shrink-0" />
      )}
      <div className="min-w-0">
        <h3 className="text-sm uppercase tracking-wider text-white/50">{t('profile.phoneTitle')}</h3>
        <p className="mt-1 text-sm text-white/60">{t('profile.phoneHint')}</p>
      </div>
    </section>
  );
}

/**
 * The password, asked for again before anything can be changed.
 *
 * Being signed in is not proof on a club tablet: the board sits on a table
 * with two accounts on it, and whoever walks past could otherwise rename the
 * player who forgot to sign out. The answer is kept only while this screen is
 * open, and every change made afterwards carries it to the server.
 */
function PasswordGate({
  token,
  onUnlocked,
  onError,
}: {
  token: string;
  onUnlocked: (password: string) => void;
  onError: (m: string | null) => void;
}) {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const t = useT();

  const submit = async (): Promise<void> => {
    setBusy(true);
    onError(null);
    try {
      await confirmPassword(token, value);
      onUnlocked(value);
    } catch (e) {
      onError(e instanceof Error ? e.message : t('profile.wrongPassword'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel flex flex-col gap-3">
      <p className="text-sm text-white/60 max-sm:min-h-[2.5rem]">{t('profile.gateHint')}</p>
      <div>
        <span className="label">{t('profile.password')}</span>
        <input
          type="password"
          className="field"
          value={value}
          autoFocus
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && value !== '' && !busy) void submit();
          }}
        />
      </div>
      <button
        type="button"
        disabled={busy || value === ''}
        onClick={() => void submit()}
        className="btn bg-white text-hud-bg"
      >
        {busy ? t('profile.checking') : t('profile.unlock')}
      </button>
    </section>
  );
}

function EditPanel({
  token,
  player,
  password,
  onUpdated,
  onError,
}: {
  token: string;
  player: PlayerProfile;
  password: string;
  onUpdated: (p: PlayerProfile) => void;
  onError: (m: string | null) => void;
}) {
  const [firstName, setFirstName] = useState(player.firstName);
  const [lastName, setLastName] = useState(player.lastName);
  const [country, setCountry] = useState(player.country);
  const [clubName, setClubName] = useState(player.club?.name ?? '');
  const [clubCountry, setClubCountry] = useState(player.club?.country ?? '');
  const [clubCity, setClubCity] = useState(player.club?.city ?? '');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [cropping, setCropping] = useState<File | null>(null);
  const t = useT();

  const dirty = useMemo(
    () =>
      firstName !== player.firstName ||
      lastName !== player.lastName ||
      country !== player.country ||
      clubName !== (player.club?.name ?? '') ||
      clubCountry !== (player.club?.country ?? '') ||
      clubCity !== (player.club?.city ?? ''),
    [firstName, lastName, country, clubName, clubCountry, clubCity, player],
  );

  const save = async (): Promise<void> => {
    setBusy(true);
    onError(null);
    setSaved(false);
    try {
      const club = clubName.trim()
        ? { name: clubName, country: clubCountry, city: clubCity }
        : null;
      onUpdated(await updateMe(token, { firstName, lastName, country, club, password }));
      setSaved(true);
    } catch (e) {
      onError(e instanceof Error ? e.message : t('profile.saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  const changeAvatar = async (file: File): Promise<void> => {
    setBusy(true);
    onError(null);
    try {
      onUpdated(await uploadAvatar(token, file, password));
    } catch (e) {
      onError(e instanceof Error ? e.message : t('profile.avatarFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel flex flex-col gap-3">
      <div className="flex gap-2">
        <div className="flex-1">
          <span className="label">{t('profile.firstName')}</span>
          <input className="field" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
        </div>
        <div className="flex-1">
          <span className="label">{t('profile.lastName')}</span>
          <input className="field" value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </div>
      </div>

      <CountryPicker value={country} onChange={setCountry} label={t('profile.nationality')} />

      <div>
        <span className="label">{t('profile.club')}</span>
        <input
          className="field"
          placeholder={t('profile.noClub')}
          value={clubName}
          onChange={(e) => setClubName(e.target.value)}
        />
      </div>

      {clubName.trim() !== '' && (
        <>
          <CountryPicker
            value={clubCountry}
            onChange={setClubCountry}
            label={t('profile.clubCountry')}
          />
          <div>
            <span className="label">{t('profile.clubCity')}</span>
            <input className="field" value={clubCity} onChange={(e) => setClubCity(e.target.value)} />
          </div>
        </>
      )}

      <div>
        <span className="label">{t('profile.avatar')}</span>
        <input
          type="file"
          accept={IMAGE_ACCEPT}
          onChange={(e) => {
            const file = e.target.files?.[0];
            // The square is chosen first; nothing is uploaded until it is.
            if (file) setCropping(file);
            e.target.value = '';
          }}
          className="w-full text-sm text-white/70 file:mr-3 file:rounded-lg file:border-0
                     file:bg-white/10 file:px-3 file:py-2 file:text-white"
        />
      </div>

      {cropping && (
        <AvatarCropper
          file={cropping}
          onCancel={() => setCropping(null)}
          onCropped={(cropped) => {
            setCropping(null);
            void changeAvatar(cropped);
          }}
        />
      )}

      <button
        type="button"
        disabled={busy || !dirty}
        onClick={() => void save()}
        className="btn bg-white text-hud-bg"
      >
        {busy ? t('profile.saving') : t('profile.save')}
      </button>
      {saved && !dirty && <p className="text-sm text-ball-green">{t('profile.saved')}</p>}
    </section>
  );
}

function DangerPanel({
  token,
  password,
  onClosed,
  onError,
}: {
  token: string;
  password: string;
  onClosed: () => void;
  onError: (m: string | null) => void;
}) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const t = useT();

  const close = async (): Promise<void> => {
    setBusy(true);
    onError(null);
    try {
      await closeAccount(token, password, DELETE_CONFIRMATION);
      onClosed();
    } catch (e) {
      onError(e instanceof Error ? e.message : t('profile.closeFailed'));
    } finally {
      setBusy(false);
    }
  };

  // Case-insensitively typed, capital-letter comparison: the word has to be
  // written out, but a phone keyboard that quietly lowercases it should not
  // turn a deliberate act into a puzzle.
  const confirmed = typed.trim().toUpperCase() === DELETE_CONFIRMATION;

  return (
    <section className="panel flex flex-col gap-3 ring-1 ring-ball-red/40">
      <p className="text-sm text-white/70">{t('profile.dangerHint')}</p>
      <div>
        <span className="label">
          {t('profile.typeToConfirm', { word: DELETE_CONFIRMATION })}
        </span>
        <input
          className="field"
          value={typed}
          autoFocus
          placeholder={DELETE_CONFIRMATION}
          onChange={(e) => setTyped(e.target.value)}
        />
      </div>
      <button
        type="button"
        disabled={busy || !confirmed}
        onClick={() => void close()}
        className="btn bg-ball-red disabled:opacity-40"
      >
        {busy ? t('profile.closing') : t('profile.close')}
      </button>
    </section>
  );
}
